import { FormConfig, MultiSelectPanel as MultiSelectPanelType, GenericMultiSelectPanel } from "@/app/config/form-config";
import { useState, useEffect } from "react";
import Image from "next/image";
import { getAssetPath } from "@/app/utils/pathUtils";
import { trackContinueButtonClick } from "@/app/utils/web-analytics";

interface MultiSelectPanelProps {
  panel: MultiSelectPanelType | GenericMultiSelectPanel;
  onAnswer: (value: string) => void;
  onBack?: () => void;
  showBack?: boolean;
  isSliding?: boolean;
  isAfterFirstPanel?: boolean;
  initialValue?: string;
}

export const MultiSelectPanel = ({ 
  panel, 
  onAnswer, 
  onBack, 
  showBack, 
  isSliding, 
  isAfterFirstPanel,
  initialValue = ""
}: MultiSelectPanelProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(initialValue ? initialValue.split(',') : []);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Set the initial value
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCheckboxChange = (value: string) => {
    setSelectedOptions(prevOptions => {
      if (prevOptions.includes(value)) {
        return prevOptions.filter(option => option !== value);
      } else {
        return [...prevOptions, value];
      }
    });
  };

  const handleContinue = () => {
    // Track the continue button click
    trackContinueButtonClick(panel.id);
    
    // If not required, we can submit even with empty selection
    // If required, only submit if at least one option is selected
    if (!panel.required || selectedOptions.length > 0) {
      setIsSubmitted(true);
      setTimeout(() => {
        // Join selected values with commas and pass to onAnswer
        onAnswer(selectedOptions.join(','));
      }, 400);
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      {isAfterFirstPanel ? (
        <h2 className="panel-secondary">
          {panel.question}
        </h2>
      ) : (
        <h1 className="panel-title">
          <span>
            {panel.question}
          </span>
        </h1>
      )}
      <div className="flex flex-col form-question-container">
        <div className="w-full">
            {panel.options.map((option) => (
              <label 
                key={option.value} 
                htmlFor={option.value}
                className={`checkbox-button ${selectedOptions.includes(option.value) ? 'checkbox-button--selected' : ''}`}
              >
                <span className="checkbox-button-label">
                  {option.text}
                </span>
                <input
                  type="checkbox"
                  id={option.value}
                  value={option.value}
                  checked={selectedOptions.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                  className="form-checkbox"
                  tabIndex={0}
                  aria-label={option.text}
                />
              </label>
            ))}
          </div>
          
          <p className="mt-4 mb-2 text-sm text-muted-foreground text-center sm:text-left">Please select all that apply.</p>
          
          <div className="flex justify-center w-full">
            <button
              onClick={handleContinue}
              className="btn-continue"
              tabIndex={0}
              aria-label="Continue"
              disabled={isSubmitted}
            >
            Continue
            <svg
              className="continue-arrow w-5 h-5 transition-transform duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
            {isSubmitted && (
              <div className="form-input-checkmark">
                <Image
                  src={getAssetPath("/images/gray-checkmark.svg")}
                  alt="Submitted"
                  width={24}
                  height={24}
                  className="animate-fade-in"
                  priority
                />
              </div>
            )}
          </button>
          </div>
      </div>
    </div>
  );
}; 